import isEqual from 'lodash/isEqual';
import {
  type ProjectionField,
  type ProjectionItem,
  type RegisterResponse,
  type RegistryGroup,
} from '@common/types/api';

export enum RegisterValueType {
  Slider = 'Slider',
  Number = 'Integer',
  Enum = 'Enumeration',
  String = 'Text',
  Flags = 'Flags',
  PosixTime = 'PosixTime',
  Boolean = 'Boolean',
}

export enum ProjectionValueType {
  Time = 1,
  Date = 2,
  Boolean = 3,
  ShortDate = 4,
  Time15 = 5,
  DisplayValue = 6,
}

export enum RegisterSpecialValuesState {
  Disabled = 'Disabled',
  Failure = 'Failure',
  NotSupported = 'NotSupported',
  SwitchedOff = 'SwitchedOff',
}

export interface SpecialValue {
  value: number;
  state: RegisterSpecialValuesState;
}

export interface ValueName {
  value: number;
  name: string;
  confirmation: string;
}

interface Register {
  readonly id: number;
  readonly groupName: string;
  readonly name: string;
  readonly value?: number | string;
  readonly stringValue?: string;
  readonly registerId: number;
  readonly registerSpecificationGroupId: number;
  readonly registerIndex: string;
  readonly registerValue?: number;
  readonly valueNames: ValueName[];
  readonly valueType: RegisterValueType | ProjectionValueType;
  readonly unit?: string;
  readonly precision?: number;
  readonly isReadOnly: boolean;
  readonly minValue?: number;
  readonly maxValue?: number;
  readonly timeStampUtc?: string | null;
  readonly step?: number;
  readonly groupItemOrder: number;
  readonly specialValues?: SpecialValue[];
  readonly isProjection: boolean;
  readonly booleanValues?: {
    falseText: string;
    trueText: string;
  };
}

export interface RegisterGroup {
  readonly name: string;
  readonly values: Register[];
  readonly subgroups?: RegisterGroup[];
  readonly id?: number;
  // Set only when true: some of the group's values could not be loaded
  readonly missingValues?: boolean;
}

export const createRegisterFromResponse = (apiRegister: RegisterResponse): Register => ({
  id: apiRegister.registerIndex,
  groupName: apiRegister.groupName,
  name: apiRegister.registerName,
  value: apiRegister.registerValue,
  stringValue: apiRegister.stringRegisterValue,
  registerId: apiRegister.registerId,
  registerSpecificationGroupId: apiRegister.groupId,
  registerIndex: `${apiRegister.registerIndex}`,
  registerValue: apiRegister.registerValue,
  valueType: apiRegister.presentation as RegisterValueType,
  valueNames: apiRegister.valueNames?.map((value) => ({
    name: value.name,
    value: value.value,
    confirmation: value.confirmation,
  })),
  unit: apiRegister.unit === 'NotSpecified' ? undefined : apiRegister.unit,
  precision: apiRegister.precision,
  isReadOnly: apiRegister.isReadOnly,
  minValue: apiRegister.minValue,
  maxValue: apiRegister.maxValue,
  timeStampUtc: apiRegister.timeStampUtc,
  step: apiRegister.step,
  specialValues: apiRegister.specialValues,
  groupItemOrder: apiRegister.groupItemOrder,
  isProjection: false,
  booleanValues: apiRegister.presentation === RegisterValueType.Boolean
    ? {
      falseText: apiRegister.falseText,
      trueText: apiRegister.trueText,
    }
    : undefined,
});

export const emptyRegister: Register = {
  id: 0,
  registerId: 0,
  registerSpecificationGroupId: 0,
  registerIndex: '',
  groupName: '',
  name: '',
  valueNames: [],
  valueType: RegisterValueType.Number,
  isReadOnly: true,
  groupItemOrder: 0,
  isProjection: false,
};

export const isEmptyRegister = (register: Register) => isEqual(register, emptyRegister);

export const createRegisterFromProjection = (
  field: ProjectionField,
  group: RegistryGroup,
): Register => {
  const valueData: ProjectionItem | undefined = field.items?.find((item) => item.key === 'Value');

  return {
    id: field.id,
    name: field.name,
    value: valueData?.value,
    valueType: valueData?.type ?? ProjectionValueType.DisplayValue,
    registerId: 0,
    registerSpecificationGroupId: group.id,
    registerIndex: `${field.id}`,
    groupItemOrder: field.order,
    groupName: group.name,
    valueNames: [],
    isReadOnly: field.isReadOnly,
    isProjection: true,
  };
};

export default Register;
